import { makeAutoObservable } from 'mobx';
import { FormControl } from './form-control';

type GroupValidatorFn<T extends Record<string, FormControl>> = (
   controls: T
) => Partial<Record<keyof T, string>>;

export class FormGroup<T extends Record<string, FormControl>> {
   controls: T;
   private validators: GroupValidatorFn<T>[];

   constructor(controls: T, validators: GroupValidatorFn<T>[] = []) {
      makeAutoObservable(this);
      this.controls = controls;
      this.validators = validators;
   }

   get value() {
      return Object.fromEntries(Object.entries(this.controls).map(([k, c]) => [k, c.value])) as {
         [K in keyof T]: T[K]['value'];
      };
   }

   get valid() {
      return (
         Object.values(this.controls).every((c) => c.valid) &&
         Object.values(this.error).every((msg) => !msg)
      );
   }

   get error(): Partial<Record<keyof T, string>> {
      const result: Partial<Record<keyof T, string>> = {};

      for (const key in this.controls) {
         result[key] = '';
      }

      for (const validator of this.validators) {
         const errMap = validator(this.controls);
         for (const key in errMap) {
            if (errMap[key]) {
               result[key] = errMap[key]!;
            }
         }
      }

      return result;
   }

   patchValue(value: Partial<{ [K in keyof T]: T[K]['value'] }>) {
      Object.entries(value).forEach(([key, val]) => {
         if (key in this.controls) {
            this.controls[key as keyof T].setValue(val as any);
         }
      });
   }

   validate() {
      Object.values(this.controls).forEach((c) => c.validate());
   }

   reset(): void {
      Object.values(this.controls).forEach((c) => c.reset());
   }
}
